"use client";

import { useState, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useSession } from "next-auth/react";
import { FaChevronDown } from "react-icons/fa";
import Button from "@/components/buttons";
import TextComponent from "./TextComponent";
import { useCountries } from "@/hooks/useCountries";
import inputCheckIconSvg from "@/assets/icons/input-check-icon.svg";
import Image from "next/image";
import Link from "next/link";

// Common styles for input elements
const inputBaseStyle =
  "w-full bg-white rounded-xl h-10 xl:h-14 px-4 xl:px-6 text-xs xl:text-sm placeholder-neutral-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelBaseStyle = "block mb-2 font-medium text-[#444A6D] leading-tight";

// Wrapper for Stripe elements to apply custom styles
const StripeInputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={`${inputBaseStyle} flex items-center`}>
    <div className="w-full">{children}</div>
  </div>
);

const stripeElementOptions = {
  style: {
    base: {
      fontSize: "14px",
      color: "#334155", // slate-700
      "::placeholder": {
        color: "#a3a3a3", // neutral-400
        fontSize: "14px",
      },
      fontFamily: "Poppins, sans-serif",
    },
    // Responsive font size for base and placeholder
    "@media screen and (min-width: 640px)": {
      base: {
        fontSize: "16px",
        "::placeholder": {
          fontSize: "16px",
        },
      },
    },
    "@media screen and (min-width: 1280px)": {
      base: {
        fontSize: "18px",
        "::placeholder": {
          fontSize: "18px",
        },
      },
    },
    invalid: {
      color: "#ef4444", // red-500
    },
  },
};

interface PaymentFormProps {
  plan: string;
  billingCycle: "MONTHLY" | "ANNUAL";
  price: number;
  onSuccess: (details: { orderNumber: string; email: string }) => void;
  promoCode?: string;
}

function PaymentFormContent({
  plan,
  billingCycle,
  onSuccess,
  promoCode,
}: Omit<PaymentFormProps, "price"> & { promoCode?: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const { data: session } = useSession();
  const {
    countries,
    userCountry,
    loading: countriesLoading,
    error: countriesError,
  } = useCountries();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardHolder, setCardHolder] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [agreed, setAgreed] = useState(true);
  const email = useMemo(
    () => session?.user?.email || "hello@emailauditengine.com",
    [session],
  );

  // Set the detected country as default when available
  useMemo(() => {
    if (userCountry && !selectedCountry) {
      setSelectedCountry(userCountry);
    }
  }, [userCountry, selectedCountry]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!agreed) {
      setError("You must agree to the terms and conditions.");
      return;
    }
    if (!selectedCountry) {
      setError("Please select your country.");
      return;
    }
    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) return;

    setLoading(true);
    setError(null);

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
          billing_details: {
            name: cardHolder,
            email: email,
          },
        });

      if (paymentMethodError) {
        setError(
          paymentMethodError.message || "Failed to create payment method",
        );
        setLoading(false);
        return;
      }

      // Call subscription API
      const response = await fetch("/api/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          planName: plan,
          billingCycle: billingCycle,
          promoCode,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "Failed to create subscription");
        setLoading(false);
        return;
      }

      // Confirm payment if client secret is provided
      if (result.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          result.clientSecret,
        );

        if (confirmError) {
          setError(confirmError.message || "Payment confirmation failed");
          setLoading(false);
          return;
        }
      }

      // Success - call onSuccess with real data
      onSuccess({
        orderNumber: result.orderNumber,
        email: email,
      });
    } catch (error) {
      console.error("Payment error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-[26px] font-['Poppins']">
      <TextComponent type="h3" className="leading-loose text-[#05004E]">
        Payment method
      </TextComponent>

      {error && (
        <div className="rounded-lg bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex flex-col gap-y-2">
          {/* --- ROW 1: LABELS --- */}
          <div className="grid-cols-4 gap-x-4 xl:grid">
            <div className="col-span-2">
              <TextComponent type="body" className={labelBaseStyle}>
                Card information
              </TextComponent>
            </div>
            <div className="col-span-2 grid grid-cols-2 gap-x-2">
              <div>
                <TextComponent type="body" className={labelBaseStyle}>
                  Expiration Date
                </TextComponent>
              </div>
              <div>
                <TextComponent type="body" className={labelBaseStyle}>
                  CVV
                </TextComponent>
              </div>
            </div>
          </div>

          {/* --- ROW 2: INPUTS --- */}
          <div className="grid-cols-4 gap-x-4 xl:grid">
            <div className="col-span-2">
              <StripeInputWrapper>
                <CardNumberElement
                  options={{
                    ...stripeElementOptions,
                    placeholder: "1111 2222 3333 4444",
                  }}
                />
              </StripeInputWrapper>
            </div>
            <div className="col-span-2 grid grid-cols-2 gap-x-2">
              <div>
                <StripeInputWrapper>
                  <CardExpiryElement
                    options={{
                      ...stripeElementOptions,
                      placeholder: "MM/YY",
                    }}
                  />
                </StripeInputWrapper>
              </div>
              <div>
                <StripeInputWrapper>
                  <CardCvcElement
                    options={{ ...stripeElementOptions, placeholder: "123" }}
                  />
                </StripeInputWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <TextComponent type="body" className={labelBaseStyle}>
          Cardholder name
        </TextComponent>
        <input
          id="cardholder-name"
          type="text"
          value={cardHolder}
          onChange={(e) => setCardHolder(e.target.value)}
          className={inputBaseStyle}
          placeholder="Full Name on Card"
          required
        />
      </div>

      <div className="space-y-2">
        <TextComponent type="body" className={labelBaseStyle}>
          Country or origin
        </TextComponent>
        <div className="relative">
          {countriesLoading ? (
            <TextComponent
              type="body"
              className={`${inputBaseStyle} flex items-center text-neutral-400`}
            >
              Loading countries...
            </TextComponent>
          ) : countriesError ? (
            <TextComponent
              type="body"
              className={`${inputBaseStyle} flex items-center text-red-500`}
            >
              Failed to load countries
            </TextComponent>
          ) : (
            <>
              <select
                id="country"
                className={`${inputBaseStyle} appearance-none pr-10`}
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select your country
                </option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
              <FaChevronDown className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 text-indigo-950" />
            </>
          )}
        </div>
      </div>

      <div className="relative flex items-center gap-3">
        <div
          className="flex size-3 cursor-pointer items-center justify-center rounded-md border-[1.4px] border-[#EDF2F6] bg-[#F9FAFB] xl:size-4"
          onClick={() => setAgreed(!agreed)}
        >
          {agreed && (
            <Image
              src={inputCheckIconSvg}
              alt="Checked Icon"
              className="size-full"
            />
          )}
        </div>
        <TextComponent
          type="body"
          className="flex cursor-pointer font-medium leading-7 text-slate-600"
          onClick={() => setAgreed(!agreed)}
          renderHTML
        >
          <span>{"I agree to Email Audit Engine's "}</span>
          <Link
            href="https://emailauditengine.com/resources/terms-and-conditions"
            className="px-1 text-kd-lte-blue hover:text-gray-700"
            target="_blank"
          >
            {" Terms of Service"}
          </Link>
          <span className="ml-1">and</span>
          <Link
            href="https://emailauditengine.com/Resources/privacy-policy"
            className="px-1 text-kd-lte-blue"
            target="_blank"
          >
            {" Privacy Policy"}
          </Link>
          .
        </TextComponent>
      </div>

      <Button
        className="h-[30px] w-[186px] xl:h-10 xl:w-[253px]"
        type="submit"
        disabled={
          !stripe || loading || !agreed || countriesLoading || !selectedCountry
        }
      >
        {loading
          ? "Processing..."
          : countriesLoading
            ? "Loading..."
            : "Continue"}
      </Button>
    </form>
  );
}

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
);

export function PaymentForm(props: PaymentFormProps) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  );
}
